import { Flask, Plus, Trash } from '@phosphor-icons/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'src/components/Button';
import {
  CallSelectorState,
  CallSelectorViewModal,
} from 'src/components/CallSelector';
import { InfoPanel } from 'src/components/InfoPanel';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { Modal } from 'src/components/Modal';
import { TestResultPanel } from 'src/components/insights/PostCallDataExtractionModal/TestResultPanel';
import { SelectBox } from 'src/components/SelectBox';
import { Textarea, TextareaSubTextPosition } from 'src/components/Textarea';
import { INSIGHT_TYPE_OPTIONS } from 'src/components/insights/constants';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import {
  Agent,
  Insight,
  InsightType,
  PostCallAnalysisResultItem,
} from 'src/interfaces/agent.interface';
import {
  Call,
  getAgentId,
  PreviewPostCallAnalysisResult,
} from 'src/interfaces/call.interface';
import { cn } from 'src/utils/className';
import { getErrorCode, getHttpStatus } from 'src/utils/requestUtils';
import { useInsights } from 'src/pages/Agents/contexts/InsightsContext';

const POST_CALL_INSIGHT_ID_PATTERN = /^[a-z0-9-]*$/;

type ModalView = 'edit' | 'selectCall';

type PostCallDataExtractionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  agent: Agent;
  insight: Insight | null;
  existingInsights: Insight[];
  onSave: (insight: Insight) => void;
  onReplace?: (oldId: string, newId: string) => void;
  onDelete?: (id: string) => void;
  disabled?: boolean;
};

const sanitizeId = (raw: string) =>
  raw
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .slice(0, 80);

const generateIdFromLabel = (label: string) => sanitizeId(label);

// Cache for agent names
const agentNameCache: Record<string, string> = {};

export const PostCallDataExtractionModal: React.FC<
  PostCallDataExtractionModalProps
> = ({
  isOpen,
  onClose,
  agent,
  insight,
  existingInsights,
  onSave,
  onReplace,
  onDelete,
  disabled = false,
}) => {
  const notification = useNotification();
  const { get, post } = useAuthedRequest();
  const {
    createInsight,
    updateInsight,
    listInsights,
    getAgentCountForInsight,
  } = useInsights();

  const isEditing = !!insight;

  // Form state
  const [label, setLabel] = useState('');
  const [id, setId] = useState('');
  const [instruction, setInstruction] = useState('');
  const [type, setType] = useState<InsightType | null>(null);
  const [enumOptions, setEnumOptions] = useState<string[]>([]);

  // View state
  const [view, setView] = useState<ModalView>('edit');

  // Saving state
  const [isSaving, setIsSaving] = useState(false);

  // Sharing count state
  const [agentCount, setAgentCount] = useState<number | null>(null);

  // Shared insight confirmation dialog
  const [showSharedConfirm, setShowSharedConfirm] = useState(false);

  // Test state
  const [testCall, setTestCall] = useState<Call | null>(null);
  const [testAgentName, setTestAgentName] = useState<string>('');
  const [testResult, setTestResult] =
    useState<PostCallAnalysisResultItem | null>(null);
  const [isEvaluating, setIsEvaluating] = useState(false);

  // Call selector state (preserved when switching views)
  const [callSelectorState, setCallSelectorState] =
    useState<CallSelectorState | null>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) return;

    if (insight) {
      setLabel(insight.label || '');
      setId(insight.id);
      setInstruction(insight.instructions || '');
      setType(insight.type);
      setEnumOptions(insight.enum_options || []);
    } else {
      setLabel('');
      setId('');
      setInstruction('');
      setType(null);
      setEnumOptions([]);
    }

    // Reset test state when modal opens
    setView('edit');
    setTestCall(null);
    setTestAgentName('');
    setTestResult(null);
    setIsEvaluating(false);
    setCallSelectorState(null);
    setShowSharedConfirm(false);
    setIsSaving(false);
    setAgentCount(null);
  }, [isOpen, insight]);

  // Fetch agent count when editing an existing insight
  useEffect(() => {
    if (!isOpen || !insight) return;

    let cancelled = false;
    const fetchAgentCount = async () => {
      try {
        const count = await getAgentCountForInsight(insight.id);
        if (!cancelled) setAgentCount(count);
      } catch {
        if (cancelled) return;
        notification.error('Failed to load insight agent usage count.');
      }
    };
    void fetchAgentCount();

    return () => {
      cancelled = true;
    };
  }, [isOpen, insight, getAgentCountForInsight, notification]);

  const instructionPlaceholder = useMemo(() => {
    switch (type) {
      case InsightType.BOOLEAN:
        return 'e.g., The customer consented to be contacted via SMS.';
      case InsightType.NUMBER:
        return 'e.g., The number of issues the customer reported.';
      case InsightType.ENUM:
        return 'e.g., Which plan was the customer interested in?';
      case InsightType.STRING:
        return 'e.g., Give a 2-3 sentence summary of the call.';
      default:
        return 'Select a type to see an example';
    }
  }, [type]);

  const trimmedLabel = useMemo(() => label.trim(), [label]);
  const trimmedInstruction = useMemo(() => instruction.trim(), [instruction]);
  const trimmedId = useMemo(() => id.trim(), [id]);

  const isEnumType = type === InsightType.ENUM;
  const normalizedEnumOptions = useMemo(
    () => enumOptions.map((o) => o.trim()),
    [enumOptions],
  );

  const isDuplicateId = useMemo(() => {
    if (!trimmedId) return false;
    return existingInsights.some(
      (i) => i.id === trimmedId && (!insight || i.id !== insight.id),
    );
  }, [existingInsights, insight, trimmedId]);

  const isFormValid = useMemo(() => {
    if (!trimmedLabel) return false;
    if (!trimmedInstruction) return false;
    if (!trimmedId) return false;
    if (!type) return false;

    if (isEnumType) {
      if (normalizedEnumOptions.length === 0) return false;
      if (normalizedEnumOptions.some((o) => !o)) return false;
    }

    return true;
  }, [
    isEnumType,
    normalizedEnumOptions,
    trimmedId,
    trimmedInstruction,
    trimmedLabel,
    type,
  ]);

  const canTest = !disabled && !isSaving && isFormValid;
  const isAgentCountLoading = isEditing && agentCount === null;
  const canSave = canTest && !isAgentCountLoading;

  const canAutoGenerateId = !isEditing;

  const handleLabelChange = (nextLabel: string) => {
    setLabel(nextLabel);
    if (!canAutoGenerateId) return;
    setId(generateIdFromLabel(nextLabel));
  };

  const handleIdChange = (raw: string) => {
    const next = sanitizeId(raw);
    setId(next);
  };

  const handleAddChoice = () => {
    setEnumOptions((prev) => [...prev, '']);
  };

  const handleChangeChoiceAt = (idx: number, value: string) => {
    setEnumOptions((prev) => prev.map((v, i) => (i === idx ? value : v)));
  };

  const handleDeleteChoiceAt = (idx: number) => {
    setEnumOptions((prev) => prev.filter((_, i) => i !== idx));
  };

  // --- Build the payload from current form state ---

  const buildPayload = useCallback(
    () => ({
      label: trimmedLabel || undefined,
      instructions: trimmedInstruction,
      type: type!,
      ...(type === InsightType.ENUM && {
        enum_options: normalizedEnumOptions,
      }),
    }),
    [trimmedLabel, trimmedInstruction, type, normalizedEnumOptions],
  );

  // --- Create flow (new insight) ---

  const handleCreateInsight = useCallback(async () => {
    setIsSaving(true);
    try {
      const created = await createInsight({
        id: trimmedId,
        ...buildPayload(),
      });
      onSave(created);
    } catch (error: unknown) {
      const status = getHttpStatus(error);
      const code = getErrorCode(error);

      if (status === 409 || code === 'Conflict') {
        notification.error('An insight with this ID already exists.');
      } else {
        notification.error('Failed to create insight.');
      }
    } finally {
      setIsSaving(false);
    }
  }, [createInsight, buildPayload, trimmedId, onSave, notification]);

  // --- Update flow (edit insight for all agents) ---

  const handleUpdateInsight = useCallback(async () => {
    if (!insight) return;
    setIsSaving(true);
    try {
      const updated = await updateInsight(insight.id, buildPayload());
      onSave(updated);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to update insight.';
      notification.error(message);
    } finally {
      setIsSaving(false);
      setShowSharedConfirm(false);
    }
  }, [updateInsight, insight, buildPayload, onSave, notification]);

  // --- "Save as New Insight" flow ---

  const handleSaveAsNew = useCallback(async () => {
    if (!insight || !onReplace) return;
    setIsSaving(true);
    try {
      // Generate a non-conflicting ID based on the label
      const base = sanitizeId(trimmedLabel).replace(/-\d+$/, '');
      const existing = await listInsights(base);
      const existingIds = new Set(existing.map((i) => i.id));

      let newId = base;
      let suffix = 1;
      while (existingIds.has(newId)) {
        newId = `${base}-${suffix}`;
        suffix++;
      }

      const created = await createInsight({
        id: newId,
        ...buildPayload(),
      });

      onReplace(insight.id, created.id);
    } catch (error: unknown) {
      const status = getHttpStatus(error);
      const code = getErrorCode(error);

      if (status === 409 || code === 'Conflict') {
        notification.error('An insight with this ID already exists.');
        return;
      }
      notification.error('Failed to save as new insight.');
    } finally {
      setIsSaving(false);
      setShowSharedConfirm(false);
    }
  }, [
    insight,
    onReplace,
    trimmedLabel,
    createInsight,
    listInsights,
    buildPayload,
    notification,
  ]);

  // --- Validation + dispatch to correct flow ---

  const handleSave = async () => {
    if (!trimmedLabel) {
      notification.error('Description is required.');
      return;
    }

    if (!trimmedInstruction) {
      notification.error(
        'Instructions for determining this insight are required.',
      );
      return;
    }

    if (!trimmedId) {
      notification.error('ID is required.');
      return;
    }
    if (!POST_CALL_INSIGHT_ID_PATTERN.test(trimmedId)) {
      notification.error(
        'ID can only contain lowercase letters, numbers, and dashes.',
      );
      return;
    }

    if (!type) {
      notification.error('Type is required.');
      return;
    }

    if (type === InsightType.ENUM) {
      const normalized = normalizedEnumOptions.filter(Boolean);
      if (normalized.length === 0) {
        notification.error('At least one choice is required.');
        return;
      }
      if (normalized.length !== normalizedEnumOptions.length) {
        notification.error('Choices cannot be empty.');
        return;
      }
    }

    if (isDuplicateId) {
      notification.error('Another call insight already uses this ID.');
      return;
    }

    if (!isEditing) {
      // New insight -- create via API
      await handleCreateInsight();
      return;
    }

    // Editing existing -- check if shared
    if (agentCount !== null && agentCount > 1) {
      setShowSharedConfirm(true);
      return;
    }

    // Not shared (or count not loaded yet) -- update directly
    await handleUpdateInsight();
  };

  const handleDelete = () => {
    if (!insight || !onDelete) return;
    onDelete(insight.id);
  };

  // Test functionality
  const handleTestClick = () => {
    setView('selectCall');
  };

  const handleStopTesting = () => {
    setTestCall(null);
    setTestAgentName('');
    setTestResult(null);
    setIsEvaluating(false);
    setCallSelectorState(null);
  };

  const runEvaluation = useCallback(
    async (call: Call) => {
      if (!canTest) {
        return;
      }

      setIsEvaluating(true);
      setTestResult(null);

      try {
        const previewInsightPayload = {
          id: trimmedId,
          label: trimmedLabel,
          instructions: trimmedInstruction,
          type: type!,
          ...(type === InsightType.ENUM && {
            enum_options: normalizedEnumOptions,
          }),
        };

        const response: PreviewPostCallAnalysisResult = await post(
          `/calls/${call.id}/insights/preview`,
          { items: [previewInsightPayload] },
        );

        const result = response.contentJson[trimmedId] || null;
        if (!result) {
          notification.error(
            'No result came back for this test. Please try again.',
          );
          return;
        }
        setTestResult(result);
      } catch (error) {
        console.error('Error evaluating insight:', error);
        notification.error('Failed to evaluate insight. Please try again.');
      } finally {
        setIsEvaluating(false);
      }
    },
    [
      type,
      canTest,
      trimmedId,
      trimmedLabel,
      trimmedInstruction,
      normalizedEnumOptions,
      post,
      notification,
    ],
  );

  const handleSelectCall = useCallback(
    async (call: Call, selectorState: CallSelectorState) => {
      setTestCall(call);
      // Store the selector state for when user clicks "Change"
      setCallSelectorState(selectorState);
      // Reset previous result so evaluation runs again
      setTestResult(null);

      // Fetch agent name
      const agentId = getAgentId(call.agent);
      if (agentId) {
        if (agentNameCache[agentId]) {
          setTestAgentName(agentNameCache[agentId]);
        } else {
          try {
            const fetchedAgent = await get(`/agents/${agentId}`);
            agentNameCache[agentId] = fetchedAgent.name;
            setTestAgentName(fetchedAgent.name);
          } catch {
            agentNameCache[agentId] = '(deleted)';
            setTestAgentName('(deleted)');
          }
        }
      } else {
        setTestAgentName('(deleted)');
      }

      setView('edit');
      // Run evaluation after selecting call
      runEvaluation(call);
    },
    [get, runEvaluation],
  );

  const handleCancelSelection = () => {
    setView('edit');
  };

  const handleChangeTestCall = () => {
    setView('selectCall');
  };

  const handleRetest = () => {
    if (testCall) {
      void runEvaluation(testCall);
    }
  };

  const title = isEditing ? 'Edit Call Insight' : 'Add Call Insight';
  const isTesting = testCall !== null;
  const insightModalMaxWidthClass = isTesting ? 'lg:max-w-6xl' : 'lg:max-w-3xl';

  // Render call selector view
  if (view === 'selectCall') {
    return (
      <CallSelectorViewModal
        isOpen={isOpen}
        onSelectCall={handleSelectCall}
        onCancel={handleCancelSelection}
        onClose={onClose}
        initialState={callSelectorState || undefined}
        defaultAgent={agent.id ? agent : undefined}
      />
    );
  }

  // Render shared insight confirmation dialog
  if (showSharedConfirm && insight) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={() => setShowSharedConfirm(false)}
        title="Save Shared Insight"
        showClose
        showModalBorder
        className={cn('transition-all duration-300', insightModalMaxWidthClass)}
        footer={
          <div className="flex items-center justify-between gap-4 pt-4 px-6 pb-6 border-t border-neutral-200">
            <Button
              variant="outlined"
              onClick={() => setShowSharedConfirm(false)}
              disabled={isSaving}
              size="small"
            >
              Cancel
            </Button>
            <div className="flex justify-end gap-2">
              {onReplace && (
                <Button
                  variant="outlined"
                  onClick={handleSaveAsNew}
                  disabled={isSaving}
                  size="small"
                >
                  Save as New Insight
                </Button>
              )}
              <Button
                onClick={handleUpdateInsight}
                disabled={isSaving}
                size="small"
              >
                Edit Insight for All Agents
              </Button>
            </div>
          </div>
        }
      >
        <InfoPanel
          variant="warning"
          className="my-6"
          title={<>This insight is used by {agentCount} agents.</>}
          description={
            <span className="text-base text-neutral-600">
              How would you like to proceed?
            </span>
          }
        />
      </Modal>
    );
  }

  const renderFooter = () => (
    <div className="flex justify-between gap-4 pt-4 px-6 pb-6 flex-shrink-0 border-t border-neutral-200">
      <div>
        {isEditing && onDelete && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleDelete}
            disabled={disabled}
            size="small"
          >
            Delete
          </Button>
        )}
      </div>
      <div className="flex flex-col items-end gap-2">
        <div className="flex gap-2">
          {isTesting ? (
            <Button variant="outlined" onClick={handleStopTesting} size="small">
              Close Test Panel
            </Button>
          ) : (
            <Button
              variant="outlined"
              onClick={handleTestClick}
              disabled={!canTest}
              size="small"
            >
              <Flask className="w-4 h-4 mr-2" weight="fill" />
              Test Insight
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={onClose}
            size="small"
            disabled={disabled}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave} size="small">
            {isEditing ? 'Apply Changes' : 'Add Call Insight'}
          </Button>
        </div>

        {/* Sharing count info */}
        {isEditing && agentCount !== null && agentCount > 1 && (
          <div className="text-sm text-neutral-500 text-right">
            This insight is shared by{' '}
            <span className="font-medium text-primary-600">
              {agentCount} agents
            </span>
            .
          </div>
        )}
      </div>
    </div>
  );

  // Render edit view
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle="Specify what you want to learn from each call."
      footer={renderFooter()}
      showModalBorder
      showClose
      className={cn('transition-all duration-300', insightModalMaxWidthClass)}
    >
      {/* Flex container for main content and test panel */}
      <div className="flex -mx-6">
        {/* Main form */}
        <div
          className={cn(
            'flex flex-col flex-1 p-6',
            isTesting && 'border-r border-neutral-200',
          )}
        >
          <div className="space-y-6">
            <Input
              label="Description"
              id="post-call-analysis-item-label"
              placeholder="e.g., Customer consent to SMS"
              value={label}
              onChange={(e) => handleLabelChange(e.target.value)}
              disabled={disabled}
              required
            />

            <Input
              label="ID"
              id="post-call-analysis-item-id"
              placeholder="e.g., customer-consent"
              value={id}
              onChange={(e) => handleIdChange(e.target.value)}
              disabled={disabled || isEditing}
              required
            />

            <div>
              <Label required>Type</Label>
              <SelectBox
                value={
                  type
                    ? INSIGHT_TYPE_OPTIONS.find((o) => o.value === type) || null
                    : null
                }
                options={INSIGHT_TYPE_OPTIONS}
                onChange={(val) => setType(val as InsightType)}
                disabled={disabled}
              />
            </div>

            <Textarea
              label="How to determine this insight"
              subText="Write brief instructions for determining the value from the call."
              subTextPosition={TextareaSubTextPosition.ABOVE}
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              rows={6}
              className="resize-none"
              disabled={disabled}
              placeholder={instructionPlaceholder}
              required
            />

            {type === InsightType.ENUM && (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-body font-medium text-primary-body">
                      Choices
                    </div>
                    <p className="text-description text-neutral-400 mt-2">
                      Add the allowed choices for this selector.
                    </p>
                  </div>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAddChoice}
                    disabled={disabled}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Choice
                  </Button>
                </div>

                <div className="space-y-2">
                  {enumOptions.map((opt, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input
                        id={`post-call-analysis-item-choice-${idx}`}
                        placeholder="e.g., yes"
                        value={opt}
                        onChange={(e) =>
                          handleChangeChoiceAt(idx, e.target.value)
                        }
                        disabled={disabled}
                        className="flex-1"
                        required
                      />
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => handleDeleteChoiceAt(idx)}
                        disabled={disabled}
                        className="h-10 px-3 flex items-center justify-center"
                      >
                        <Trash className="w-5 h-5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Test Result Panel */}
        {isTesting && (
          <TestResultPanel
            testCall={testCall!}
            agentName={testAgentName}
            result={testResult}
            isEvaluating={isEvaluating}
            canRetest={canTest}
            onChangeTestCall={handleChangeTestCall}
            onRetest={handleRetest}
            notification={notification}
          />
        )}
      </div>
    </Modal>
  );
};
